import React, { useState, useEffect } from 'react';
import './GroupDetailModal.css';
import { getToken } from '../../../../services/api';

const GroupDetailModal = ({ groupId, estado, onClose, onGroupUpdate }) => {
  const [groupData, setGroupData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [showStatusForm, setShowStatusForm] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [addingComment, setAddingComment] = useState(false);

  useEffect(() => {
    fetchGroupDetails();
  }, [groupId]);

  const fetchGroupDetails = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_VERCEL_API_URL : process.env.REACT_APP_LOCAL_API_URL;
      const response = await fetch(`${API_URL}/precatorio-group-status/comercial/group/${groupId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar detalhes do grupo');
      }

      const data = await response.json();
      setGroupData(data);
      setSelectedStatus(data.groupStatus.groupStatus);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    if (!statusReason.trim()) {
      alert('Por favor, forneça um motivo para a mudança de status');
      return;
    }

    try {
      setUpdatingStatus(true);
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_VERCEL_API_URL : process.env.REACT_APP_LOCAL_API_URL;
      const response = await fetch(`${API_URL}/precatorio-group-status/comercial/group/${groupId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          groupStatus: selectedStatus,
          reason: statusReason
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao atualizar status');
      }

      const result = await response.json();
      
      // Update local state
      setGroupData(prev => ({
        ...prev,
        groupStatus: {
          ...prev.groupStatus,
          groupStatus: selectedStatus
        }
      }));

      // Notify parent component about the update
      if (onGroupUpdate) {
        onGroupUpdate({
          _id: groupId,
          groupStatus: selectedStatus,
          commentsCount: groupData.groupStatus.comments.length,
          lastComment: groupData.groupStatus.comments[groupData.groupStatus.comments.length - 1]
        });
      }

      setShowStatusForm(false);
      setStatusReason('');
      alert('Status atualizado com sucesso!');
    } catch (err) {
      alert('Erro ao atualizar status: ' + err.message);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) {
      alert('Por favor, digite um comentário');
      return;
    }

    try {
      setAddingComment(true);
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_VERCEL_API_URL : process.env.REACT_APP_LOCAL_API_URL;
      const response = await fetch(`${API_URL}/precatorio-group-status/comercial/group/${groupId}/comment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: commentText
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao adicionar comentário');
      }

      const result = await response.json();
      const newComment = result.comment;

      // Update local state with the new comment
      setGroupData(prev => ({
        ...prev,
        groupStatus: {
          ...prev.groupStatus,
          comments: [...prev.groupStatus.comments, newComment]
        }
      }));

      // Notify parent component about the update
      if (onGroupUpdate) {
        onGroupUpdate({
          _id: groupId,
          groupStatus: groupData.groupStatus.groupStatus,
          commentsCount: groupData.groupStatus.comments.length + 1,
          lastComment: newComment
        });
      }

      setCommentText('');
      alert('Comentário adicionado com sucesso!');
    } catch (err) {
      alert('Erro ao adicionar comentário: ' + err.message);
    } finally {
      setAddingComment(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDocument = (type, number) => {
    if (type === 'CPF') {
      return number.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (type === 'CNPJ') {
      return number.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return number;
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Em Contato':
        return 'status-badge status-em-contato';
      case 'Vendendo':
        return 'status-badge status-vendendo';
      case 'Não Vende':
        return 'status-badge status-nao-vende';
      default:
        return 'status-badge status-nao-contatou';
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="loading">Carregando detalhes...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="error">Erro: {error}</div>
          <button onClick={onClose} className="close-button">Fechar</button>
        </div>
      </div>
    );
  }

  if (!groupData) {
    return null;
  }

  const { groupStatus, precatorios } = groupData;

  return (
    <div className="modal-overlay" onClick={(e) => e.target.className === 'modal-overlay' && onClose()}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>Detalhes do Grupo</h2>
          <button onClick={onClose} className="close-button">×</button>
        </div>

        <div className="modal-body">
          {/* Group Info Section */}
          <div className="section">
            <h3>Informações do Grupo</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Tipo:</span>
                <span className={`document-type-badge ${groupStatus.documentType}`}>
                  {groupStatus.documentType}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Documento:</span>
                <span className="info-value document-number">
                  {formatDocument(groupStatus.documentType, groupStatus.documentNumber)}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Estado:</span>
                <span className="info-value">{estado}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Status Atual:</span>
                <span className={getStatusBadgeClass(groupStatus.groupStatus)}>
                  {groupStatus.groupStatus}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Total de Precatórios:</span>
                <span className="info-value">{groupStatus.summary.totalPrecatorios}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Valor Total:</span>
                <span className="info-value">{formatCurrency(groupStatus.summary.totalValue)}</span>
              </div>
            </div>
          </div>

          {/* Status Management Section */}
          <div className="section">
            <h3>Gerenciar Status</h3>
            {!showStatusForm ? (
              <button 
                onClick={() => setShowStatusForm(true)} 
                className="btn btn-primary"
              >
                Alterar Status
              </button>
            ) : (
              <form onSubmit={handleStatusUpdate} className="status-form">
                <div className="form-group">
                  <label>Novo Status:</label>
                  <select 
                    value={selectedStatus} 
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="form-select"
                  >
                    <option value="Não Contatou">Não Contatou</option>
                    <option value="Em Contato">Em Contato</option>
                    <option value="Vendendo">Vendendo</option>
                    <option value="Não Vende">Não Vende</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Motivo da Mudança:</label>
                  <textarea
                    value={statusReason}
                    onChange={(e) => setStatusReason(e.target.value)}
                    className="form-textarea"
                    rows="3"
                    placeholder="Descreva o motivo da mudança de status..."
                    required
                  />
                </div>
                <div className="form-actions">
                  <button 
                    type="submit" 
                    className="btn btn-success"
                    disabled={updatingStatus}
                  >
                    {updatingStatus ? 'Atualizando...' : 'Confirmar'}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowStatusForm(false);
                      setStatusReason('');
                    }}
                    className="btn btn-secondary"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Precatorios List Section */}
          <div className="section">
            <h3>Precatórios do Grupo ({precatorios.length})</h3>
            <div className="precatorios-table-container">
              <table className="precatorios-table">
                <thead>
                  <tr>
                    <th>Número</th>
                    <th>Natureza</th>
                    <th>Ente</th>
                    <th>Valor</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {precatorios.map((precatorio) => (
                    <tr key={precatorio._id}>
                      <td>{precatorio.numeroPrecatorio}</td>
                      <td>{precatorio.natureza || '-'}</td>
                      <td>{precatorio.ente || '-'}</td>
                      <td>{formatCurrency(precatorio.valorAtualizado)}</td>
                      <td>
                        <span className="precatorio-status">{precatorio.status || 'Pendente'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Comments Section */}
          <div className="section">
            <h3>Comentários ({groupStatus.comments.length})</h3>
            
            {/* Add Comment Form */}
            <form onSubmit={handleAddComment} className="comment-form">
              <div className="form-group">
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  className="form-textarea"
                  rows="3"
                  placeholder="Digite seu comentário..."
                  required
                />
              </div>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={addingComment}
              >
                {addingComment ? 'Adicionando...' : 'Adicionar Comentário'}
              </button>
            </form>

            {/* Comments List */}
            <div className="comments-list">
              {groupStatus.comments.length === 0 ? (
                <p className="no-comments">Nenhum comentário ainda.</p>
              ) : (
                groupStatus.comments.map((comment, index) => (
                  <div key={index} className="comment-item">
                    <div className="comment-header">
                      <span className="comment-author">{comment.author.userName}</span>
                      <span className="comment-date">
                        {new Date(comment.createdAt).toLocaleString('pt-BR')}
                      </span>
                    </div>
                    <div className="comment-text">{comment.text}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupDetailModal;
